import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Jute } from '../..';
import { createVCardString, getPausableVideoIdsFromIdArray, transitionAdjustedRenderOrder } from '../../utils';
import { getAnalyticsLabelForAction } from '../../utils/analytics';
import { IEntityActionTypes, IMode, ISceneActions, ITrackingImageFileData, ITransCatDict, ITransPhaseDict, IUserActions, useActionStateStore, useDataStore, useSceneStore, useUserStore } from '../../zustand';
import {
	IActionCategory,
	IButton,
	IButtonSubCategory,
	IComponentsById,
	IComponentType,
	IComponentUnion,
	IImage,
	IImageTrackedScene,
	IImageTrackingOrientation,
	IModel3d,
	IRoot,
	ISceneActionData,
	ISceneActionDict,
	ISceneComp,
	IScormEventType,
	IScreenAnchorGroup,
	IScreenContent,
	ISpatialComponentActionData,
	ISpatialComponentUnion,
	IText,
	ITrackingTypes,
	ITriggerTypes,
	ITuple3,
	IVector3,
	IVideo,
} from '../r3f-components/component-data-structure';
import { hlsVideoCache } from '../r3f-components/hooks';
import { useGetScreenRelativeComponentIds } from '../r3f-components/hooks/useGetScreenRelativeComponentIds';
import { ITransCatTypes } from '../r3f-components/transitions';
import { ITransitionPhase } from '../r3f-components/transitions/spec';
import { getFaceTrackingComponentIdsBySceneId } from '../r3f-components/utils/face-tracking';
import { getScreenRelativeComponentUserData, getUserDataFromIntersections, hasScreenRelativeIntersection, isClosestToCamera, isHighestRenderOrder, toRadians } from '../r3f-components/utils/general';
import { getAdjustedBorderWidth, getAdjustedFontSize, getAdjustedPosition, getAdjustedScale, getAllUsedImageTrackerIds, resetEntityTransforms } from './hocUtils';
import { useAllSceneEntityIds } from '../../hooks/general';
import { presetAnimationController } from '../PresetAnimationController';
import { Intersection } from 'three';
import { get360VideoIdBySceneId } from '../r3f-components/utils/content360';

export const useGetActiveSceneId = (): string => {
	const activeSceneId = useSceneStore(state => state.activeSceneId);
	const componentsById = useDataStore(state => state.componentsById);
	const rootId = useDataStore(state => state.rootId)
	return activeSceneId ?? (componentsById?.[rootId || ''] as IRoot)?.children?.[0];
}

export const useGetActiveScene = (): ISceneComp => {
	const activeSceneId = useGetActiveSceneId();
	return useGetSceneById(activeSceneId);
}

const useGetSceneById = (id: string): ISceneComp => {
	const componentsById = useDataStore(state => state.componentsById);
	return componentsById?.[id] as ISceneComp;
}

export const useGetActiveTrackingImageData = (): ITrackingImageFileData | null => {
	const scene = useGetActiveScene();
	const trackingImageDict = useDataStore(state => state.trackingImageDict);
	if (scene?.trackingType !== ITrackingTypes.image) return null;
	return trackingImageDict?.[scene?.trackingImageId] ?? null;
}

export const useIsActiveTrackingImageFlatOrientation = (): boolean => {
	const trackingImageData = useGetActiveTrackingImageData();
	return trackingImageData ? trackingImageData.orientation === IImageTrackingOrientation.flat : false;
}	

export const useGetActiveTrackingImageRadius = (): number => {
	const data = useGetActiveTrackingImageData();
	return data?.topRadius ?? 0;
}

export const useGetActiveTrackingType = (): ITrackingTypes | null => {
	const activeSceneId = useSceneStore((state) => state.activeSceneId);
	const componentsById =  useDataStore(state => state.componentsById);
	const rootId = useDataStore(state => state.rootId);
	// If there is no activeSceneId, this is the initial render, so use the first scene in the experience
	const currentSceneId =  activeSceneId ?? (componentsById?.[rootId || ''] as IRoot)?.children?.[0];
	return componentsById && currentSceneId ? (componentsById[currentSceneId] as ISceneComp).trackingType! : null;
};

export const useGetPreviousTrackingType = (): ITrackingTypes | null => {
	const previousActiveSceneId = useSceneStore((state) => state.previousSceneId);
	const componentsById =  useDataStore(state => state.componentsById);
	const rootId = useDataStore(state => state.rootId);
	return componentsById && previousActiveSceneId ? (componentsById[previousActiveSceneId] as ISceneComp).trackingType! : null;
};

export const useHasOnly360ContentScenes = (): boolean | null => {
	const componentsById =  useDataStore(state => state.componentsById);
	const rootId = useDataStore(state => state.rootId);
	if (!componentsById || !rootId) return null;
	return !(componentsById?.[rootId] as IRoot)?.children.map(id => (componentsById[id] as ISceneComp).trackingType).filter(tracking => tracking !== ITrackingTypes.content360).length
}

/**
 * Return memoized ar and screen relative mode adjusted tuple3's
 */
export const useGetAdjustedMemoSpatialProps = (entity: ISpatialComponentUnion, screenRelative: boolean): { position: ITuple3; scale: ITuple3; rotation: ITuple3; zeroTuple3: ITuple3 } => {
	const mode = useDataStore(({ mode }) => mode);
	const position = getAdjustedPosition(mode, screenRelative, entity.position as ITuple3);
	const scale = getAdjustedScale(mode, screenRelative, entity.scale as ITuple3);
	const rotation = useDegreesToMemoRadians(entity.rotation);
	const zeroTuple3 = [0, 0, 0] as ITuple3;

	return useMemo(
		() => ({
			position,
			scale,
			rotation,
			zeroTuple3,
		}),
		[]
	); // eslint-disable-line react-hooks/exhaustive-deps
};

/**
 * Return ar and screen relative mode adjusted font size
 */
export const useGetAdjustedFontSize = (entity: IButton | IText, screenRelative: boolean): number => {
	const mode = useDataStore(({ mode }) => mode);
	return getAdjustedFontSize(mode, screenRelative, entity);
};

/**
 * Return ar and screen relative mode adjusted border width
 */
export const useGetAdjustedBorderWidth = (entity: IButton | IVideo | IImage, screenRelative: boolean): number => {
	const mode = useDataStore(({ mode }) => mode);
	return getAdjustedBorderWidth(mode, screenRelative, entity);
};

/**
 * Return ref which is subscribed to the zustand store and updated transiently without triggering a re-render in React
 */
export const useGetSubscribedActiveSceneRef = (): React.RefObject<string | null> => {
	const activeSceneIdRef = useRef(useSceneStore.getState().activeSceneId);
	// Subscribe ref to store to update on state changes
	useEffect(() => useSceneStore.subscribe(({ activeSceneId }) => (activeSceneIdRef.current = activeSceneId)), []);
	return activeSceneIdRef;
};

/**
 * Return ref which is subscribed to the zustand store and updated transiently without triggering a re-render in React
 */
export const useGetSubscribedPlacementModeRef = (): React.RefObject<boolean> => {
	const placementModeRef = useRef(useSceneStore.getState().placementMode);
	// Subscribe ref to store to update on state changes
	useEffect(() => useSceneStore.subscribe((state) => (placementModeRef.current = state.placementMode)), []);
	return placementModeRef;
};

/**
 * Return ref which is subscribed to the zustand store and updated transiently without triggering a re-render in React
 */
export const useGetSubscribedARModeRef = (): React.RefObject<IMode | undefined> => {
	const arModeRef = useRef(useDataStore.getState().mode);
	// Subscribe ref to store to update on state changes
	useEffect(() => useDataStore.subscribe((state) => (arModeRef.current = state.mode)), []);
	return arModeRef;
};

/**
 * Return ref which is subscribed to the zustand store and updated transiently without triggering a re-render in React
 */
export const useGetSubscribedSceneTransitionCategoryRef = (sceneId: string): React.RefObject<ITransCatTypes> => {
	const sceneTransCatRef = useRef(useSceneStore.getState().transitionCategory[sceneId]);
	// Subscribe ref to store to update on state changes
	useEffect(() => {
		useSceneStore.subscribe(
			(state) => {
				const transitionCateogryDict = state.transitionCategory;
				const cat = transitionCateogryDict?.[sceneId];
				if (typeof cat !== 'undefined') {
					sceneTransCatRef.current = cat;
				}
			}
		);
	}, [sceneId]); // eslint-disable-line react-hooks/exhaustive-deps
	return sceneTransCatRef;
};

/**
 * Return ref which is subscribed to the zustand store and updated transiently without triggering a re-render in React
 */
export const useGetSubscribedSceneTransitionPhaseRef = (sceneId: string): React.RefObject<ITransitionPhase> => {
	const sceneTransPhaseRef = useRef(useSceneStore.getState().transitionPhase[sceneId]);
	// Subscribe ref to store to update on state changes
	useEffect(() => {
		useSceneStore.subscribe(
			(state) => {
				const transitionPhaseDict = state.transitionPhase;
				const phase = transitionPhaseDict?.[sceneId];
				if (typeof phase !== 'undefined') {
					sceneTransPhaseRef.current = phase;
				}
			}
		);
	}, [sceneId]); // eslint-disable-line react-hooks/exhaustive-deps
	return sceneTransPhaseRef;
};

interface IOnEntityTestClickInput {
	sceneTransCatRef: React.RefObject<ITransCatTypes>;
	sceneTransPhaseRef: React.RefObject<ITransitionPhase>;
	sceneId: string;
	entity: ISpatialComponentUnion;
}

/**
 * Enable simulated test clicks on entities in testing environments
 */
export const useOnEntityTestClick = ({ sceneId, entity, sceneTransCatRef, sceneTransPhaseRef }: IOnEntityTestClickInput): void => {
	const entityActionHandler = useAddEntityActionBehaviour(sceneId, entity, sceneTransCatRef, sceneTransPhaseRef, ITriggerTypes.onTap);
	useEffect(() => {
		if (Jute.environment !== 'test') return;
		Jute.onTestClickEntity((id) => {
			if (entity.id === id && entity.actions?.[ITriggerTypes.onTap]) {
				entityActionHandler(entity.actions[ITriggerTypes.onTap]!);
			}
		});
	}, [entityActionHandler, entity.actions, entity.id]);
};

export const useDegreesToMemoRadians = (r: IVector3): ITuple3 => {
	return useMemo(() => [toRadians(r[0]), toRadians(r[1]), toRadians(r[2])], [r]);
};

interface IHocPointerDownInput {
	pointerIsDownRef: React.RefObject<boolean>;
	sceneTransCatRef: React.RefObject<ITransCatTypes>;
	sceneTransPhaseRef: React.RefObject<ITransitionPhase>;
	sceneId: string;
	entity: ISpatialComponentUnion;
	screenRelative: boolean;
}

/**
 * HOC pointer down handler
 */
export const useHocPointerDown = ({
	pointerIsDownRef,
	sceneTransCatRef,
	sceneTransPhaseRef,
	sceneId,
	entity,
	screenRelative,
}: IHocPointerDownInput): ((int: Intersection[] | undefined) => void) => {
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const rootId = useDataStore(({ rootId }) => rootId);
	const firstSceneId = (componentsById?.[rootId as string] as IRoot).children?.[0];
	const scene = componentsById?.[sceneId] as ISceneComp | undefined;
	const screenRelativeIds = useGetScreenRelativeComponentIds(sceneId, componentsById);
	const activeSceneIdRef = useGetSubscribedActiveSceneRef();
	const placementModeRef = useGetSubscribedPlacementModeRef();
	const entityActionHandler = useAddEntityActionBehaviour(sceneId, entity, sceneTransCatRef, sceneTransPhaseRef, ITriggerTypes.onTap);
	const { scene: threeScene } = useThree();
	const timeoutRef = useRef<NodeJS.Timeout>(undefined);

	return useCallback(
		(intersections: Intersection[] | undefined) => {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => {
				pointerIsDownRef.current = false;
			}, 100)
			//unmuteVideosInCache(); // this is required to play videos via target actions later on as actionHandler is tied to user interaction
			if (
				pointerIsDownRef.current ||
				!intersections ||
				sceneTransPhaseRef.current !== ITransitionPhase.enterFinish ||
				placementModeRef.current ||
				(activeSceneIdRef.current !== null && activeSceneIdRef.current !== sceneId) ||
				(activeSceneIdRef.current === null && firstSceneId !== sceneId) ||
				!entity.actions?.[ITriggerTypes.onTap] ||
				!scene
			)
				return;
			pointerIsDownRef.current = true;

			const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, entity.renderOrder * 20);
			const userDataArray = getUserDataFromIntersections(intersections);
			const entityVisibilityGroup = threeScene?.getObjectByName?.(`toggleVisibilityGroup-${entity.id}`);
			if (!!entityVisibilityGroup && !entityVisibilityGroup?.visible) return;

			if (!screenRelative) {
				const sceneChildren = scene.trackingType === ITrackingTypes.face && componentsById ? getFaceTrackingComponentIdsBySceneId(sceneId, componentsById) : scene.children;

				// component is NOT screen relative: check if screen relative component intersected OR if closest to camera or highest render order of AR content
				if (hasScreenRelativeIntersection(userDataArray, screenRelativeIds, threeScene) || (!isClosestToCamera(userDataArray, renderOrder) && !isHighestRenderOrder({userDataArray, renderOrder, sceneChildrenIds: sceneChildren, threeSceneToTestVisiblity: threeScene})))
					return;
			} else {
				// component IS screen relative: don't check distance to camera & only check for highest render order out of screen relative content
				if (!isHighestRenderOrder({userDataArray: getScreenRelativeComponentUserData(userDataArray, screenRelativeIds), renderOrder, sceneChildrenIds: screenRelativeIds, threeSceneToTestVisiblity: threeScene})) return;
			}
			if (entity.actions?.[ITriggerTypes.onTap]) entityActionHandler(entity.actions[ITriggerTypes.onTap]!);
		},
		[entityActionHandler, screenRelative, screenRelativeIds]
	); //eslint-disable-line react-hooks/exhaustive-deps
};

/**
 * Invoke action specific behaviour for entity
 */
export const useAddEntityActionBehaviour = (
	sceneId: string,
	entity: ISpatialComponentUnion | ISceneComp,
	sceneTransCatRef: React.RefObject<ITransCatTypes>,
	sceneTransPhaseRef: React.RefObject<ITransitionPhase>,
	triggerType: ITriggerTypes
): ((actions: (ISpatialComponentActionData | ISceneActionData)[]) => void) => {
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const userDispatch = useUserStore(({ dispatch }) => dispatch);
	const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
	const isGrabAndGoEnabled = useUserStore(({ isGrabNGoEnabled }) => isGrabNGoEnabled);
	const trackingImageDict = useDataStore(({ trackingImageDict }) => trackingImageDict);
	const sceneVideoIds = useAllSceneEntityIds(sceneId, IComponentType.Video);
	const { scene } = useThree();

	return useCallback((actions: (ISpatialComponentActionData | ISceneActionData)[]) => {
		if (sceneTransCatRef.current === 'hidden' || sceneTransCatRef.current === 'exit') return;

		const enterTransFinished = sceneTransPhaseRef.current === 'enterFinish';
		const enterTransStart = sceneTransPhaseRef.current === 'enterStart';

		for (let i = 0; i < (actions || []).length; i++) {
			const action = actions[i];
			switch (action.type) {
				case IActionCategory.scorm:
					const { event } = action;
					switch (event) {
						case IScormEventType.progress:
							Jute.lmsClient?.setProgress(action.progress);
							break;
						case IScormEventType.completion:
							Jute.lmsClient?.setCompletion(true);
							break;
					}
					break;
				case IActionCategory.toggleVisibility:
					{
						const { targetIds } = action;
						const targetId = targetIds[0];
						const prospectiveGroupId = `toggleVisibilityGroup-${targetId}`;
						const threeGroup = scene.getObjectByName(prospectiveGroupId);
						const entity = componentsById?.[targetId]
						if (threeGroup) {
							switch (action.behaviour) {
								case 'hide':
									threeGroup.visible = false;
									if (entity?.type === IComponentType.Video) {
										entityActionDispatch?.({
											type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
											payload: { ids: [targetId] },
										});
									}
									break;
								case 'show':
									threeGroup.visible = true;
									if (entity?.type === IComponentType.Video && entity.autoplay) {
										entityActionDispatch?.({
											type: IEntityActionTypes.PLAY_VIDEOS_BY_ID,
											payload: { ids: [targetId] },
										});
									}
									break;
								case 'toggle':
									threeGroup.visible = !threeGroup.visible;
									if (entity?.type === IComponentType.Video) {
										entityActionDispatch?.({
											type: threeGroup.visible && entity.autoplay ? IEntityActionTypes.PLAY_VIDEOS_BY_ID : IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
											payload: { ids: [targetId] },
										});
									}
									break;
							}
						}
					}
					break;
				case IActionCategory.animateModel: {
					// Animate model is currently the only action that uses target actions
					const { animationName, animationRepetitions, targetIds, isClampWhenFinished } = action;
					const animationData = targetIds.map((id) => {
						const targetModel3d = componentsById?.[id] as IModel3d;
						Jute.logEventToAnalytics({
							category: '3D models',
							name: `${triggerType} animate`,
							label: getAnalyticsLabelForAction(IActionCategory.animateModel, targetModel3d.title),
							value: 1,
						});

						return {
							id,
							isPlayingAnimation: true,
							animationName,
							animationRepetitions,
							isClampWhenFinished,
						};
					});
					entityActionDispatch?.({
						type: IEntityActionTypes.PLAY_MODEL_ANIMATIONS_BY_ID,
						payload: animationData,
					});
					break;
				}
				case IActionCategory.composeEmail: {
					if (!enterTransFinished) return;
					if (typeof action.body === 'undefined') return;
					Jute.logEventToAnalytics({
						category: 'Compose email',
						name: `${triggerType} compose email`,
						label: getAnalyticsLabelForAction(IActionCategory.composeEmail, entity.title),
						value: 1,
					});
					let { recipient, subject, body } = action;
					subject = subject ? `?subject=${subject.replace(' ', '%20')}` : '';
					body = body ? `&body=${body}` : '';
					const a = document.createElement('a');
					a.href = `mailto:${recipient}${subject}${body}`;
					a.click();
					break;
				}
				case IActionCategory.linkScene: {
					Jute.logEventToAnalytics({
						category: 'Link',
						name: `${triggerType} scene link`,
						label: getAnalyticsLabelForAction(IActionCategory.linkScene, entity.title),
						value: 1,
					});
					sceneDispatch?.({
						type: ISceneActions.SET_ACTIVE_SCENE_ID,
						activeSceneId: action.sceneId as string,
					});
					if (Jute.environment === 'test') console.log('new_active_scene:', action.sceneId);
					sceneDispatch?.({
						type: ISceneActions.SET_PREVIOUS_SCENE_ID,
						previousSceneId: sceneId,
					});

					const previousScene = componentsById?.[sceneId] as IImageTrackedScene;
					const activeScene = componentsById?.[action.sceneId!] as IImageTrackedScene;

					if (!isGrabAndGoEnabled && !!activeScene.trackingImageId) {
						userDispatch?.({
							type: IUserActions.SET_IMG_TRK_START_SCENE,
							payload: {[activeScene.trackingImageId]: action.sceneId}
						})
						userDispatch?.({
							type: IUserActions.SET_ENABLED_IMAGE_TRACKER_IDS,
							payload: [activeScene.trackingImageId]
						})
					}

					// console.log({previousTrackingId: previousScene.trackingImageId, newTrackingId: activeScene.trackingImageId})
					if (!isGrabAndGoEnabled && previousScene.trackingImageId !== activeScene.trackingImageId && !!activeScene.trackingImageId) {
						userDispatch?.({
							type: IUserActions.SET_LINK_SCENE_ENABLED_IMAGE_TRACKER_ID,
							payload: activeScene.trackingImageId
						})
						userDispatch?.({
							type: IUserActions.SET_TRACKING_IMAGE_VISIBLE,
							payload: { visible: false },
						});
					}

					if (enterTransStart) {
						sceneDispatch?.({
							type: ISceneActions.SET_IMMEDIATE_EXIT_SCENE_ID,
							immediateExitSceneId: { [sceneId]: action.sceneId as string },
						});
					} else {
						const linkedSceneId = action.sceneId;
						if (typeof linkedSceneId !== 'undefined' && linkedSceneId !== null) {
							const newScene = componentsById?.[linkedSceneId] as ISceneComp;
							const previousScene = componentsById?.[sceneId] as ISceneComp;

							const trackingTypeHasChanged = newScene.trackingType !== previousScene.trackingType;

							// enable videos to play (as muted is set in this user related click handler)
							newScene.children.forEach((id) => {
								if ((componentsById?.[id] as IVideo).autoplay && hlsVideoCache?.[id]?.[1]) {
									(hlsVideoCache[id][1] as any).muted = false;
								}
							});

							const scene360VideodId = get360VideoIdBySceneId(linkedSceneId, componentsById);

							if (scene360VideodId && hlsVideoCache?.[scene360VideodId]?.[1]) {
								(hlsVideoCache[scene360VideodId][1] as any).muted = false;
							}

							// Always initiate exit transition of previous scene. Initialte enter transition of new scene immediately
							// if the tracking type hasn't changed otherwise let the ui prompts of the respective tracker component
							// initiate the enter transition of the new scene
							const transCatDict = { [sceneId]: ITransCatTypes.exit };

							if (!trackingTypeHasChanged) {
								transCatDict[linkedSceneId.toString()] = ITransCatTypes.enter;
							}

							sceneDispatch?.({
								type: ISceneActions.SET_TRANSITION_CATEGORY,
								transCatDict,
								info: 'onBtn Click: enter new scene, exit current scene',
							});

							// Update global store with tracking type change status
							sceneDispatch?.({
								type: ISceneActions.SET_TRACKING_TYPE_CHANGED,
								trackingTypeHasChanged,
							});
						}
					}
					break;
				}
				case IActionCategory.multiImageScan: {
					Jute.logEventToAnalytics({
						category: 'ImageScan',
						name: `${triggerType} image scan`,
						label: getAnalyticsLabelForAction(IActionCategory.multiImageScan, entity.title),
						value: 1,
					});

					const trackingIds = getAllUsedImageTrackerIds(componentsById);
					const firstImageTrackedSceneId =  trackingImageDict?.[trackingIds[0]]?.startSceneId;
					
					if (!firstImageTrackedSceneId || !componentsById) break;

					const {rootId} = useDataStore.getState();
					const sceneIds = (componentsById?.[rootId ?? ''] as IRoot)?.children ?? [];
					const activeSceneId = useSceneStore.getState().activeSceneId ?? sceneIds[0];
					const activeScene = componentsById[activeSceneId] as ISceneComp;
					let trackerIdsToEnable = getAllUsedImageTrackerIds(componentsById)


					if (activeScene.trackingType === ITrackingTypes.noTrackingScreen) {
						sceneDispatch?.({
							type: ISceneActions.SET_TRANSITION_PHASE,
							transPhaseDict: { [activeSceneId]: ITransitionPhase.exitFinish },
						})
					}
					
					userDispatch?.({
						type: IUserActions.SET_LINK_SCENE_ENABLED_IMAGE_TRACKER_ID,
						payload: null
					})

					userDispatch({
						type: IUserActions.SET_GRAB_N_GO_ACTIVE,
						payload: false
					})

					userDispatch({
						type: IUserActions.SET_ROUND_ROBIN_ACTIVE,
						payload: activeScene.trackingType !== ITrackingTypes.image
					})
		
					userDispatch({
						type: IUserActions.SET_TRACKING_IMAGE_VISIBLE,
						payload: { visible: false },
					});

					if (activeScene.trackingType !== ITrackingTypes.image) {
						sceneDispatch?.({
							type: ISceneActions.SET_ACTIVE_SCENE_ID,
							activeSceneId: firstImageTrackedSceneId
						});

						const transCatDict = { [sceneId]: ITransCatTypes.exit };

						sceneDispatch?.({
							type: ISceneActions.SET_TRANSITION_CATEGORY,
							transCatDict,
							info: 'onBtn Click: enter new scene, exit current scene',
						});

						// Update global store with tracking type change status
						sceneDispatch?.({
							type: ISceneActions.SET_TRACKING_TYPE_CHANGED,
							trackingTypeHasChanged: true,
						});

					}
					
					userDispatch?.({
						type: IUserActions.SET_ENABLED_IMAGE_TRACKER_IDS,
						payload: trackerIdsToEnable
					})

					break;
				}
				case IActionCategory.playSound: {
					if (!enterTransFinished) return;
					Jute.logEventToAnalytics({
						category: 'Sound',
						name: `${triggerType} play`,
						label: getAnalyticsLabelForAction(IActionCategory.playSound, action.title),
						value: 1,
					});
					let { stopMedia, loop } = action;
					if (stopMedia) {
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_ALL_VIDEOS,
						});
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
							payload: { id: sceneId, excludedIds: [(entity as ISpatialComponentUnion).id ?? sceneId] },
						});
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
						});
					}
					entityActionDispatch?.({
						type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
						payload: { sceneId, entityIds: [(entity as ISpatialComponentUnion).id ?? sceneId], loop },
					});
					break;
				}
				case IActionCategory.animateParticle: {
					if (typeof action.targetIds === 'undefined' || !componentsById) return;
					const { targetIds, behaviour, duration } = action;
					for (let i = 0; i < targetIds.length; i++) {
						const targetId = targetIds[i];
						const targetEmitter = componentsById[targetId] as IVideo;
						Jute.logEventToAnalytics({
							category: 'Particle',
							name: `${triggerType} ${behaviour} particle animation`,
							label: getAnalyticsLabelForAction(IActionCategory.animateParticle, targetEmitter.title),
							value: 1,
						});
					}
					if (behaviour === 'stop') {
						entityActionDispatch?.({
							type: IEntityActionTypes.PAUSE_EMITTER_ANIMATION_BY_ID,
							payload: targetIds,
						});
						break;
					}
					const actionData = targetIds.map((id) => ({ id, duration }));
					if (behaviour === 'toggle') {
						entityActionDispatch?.({
							type: IEntityActionTypes.TOGGLE_EMITTER_ANIMATION_BY_ID,
							payload: actionData,
						});
						break;
					}
					entityActionDispatch?.({
						type: IEntityActionTypes.PLAY_EMITTER_ANIMATION_BY_ID,
						payload: actionData,
					});
					break;
				}
				case IActionCategory.playVideo: {
					if (typeof action.targetIds === 'undefined' || !componentsById) return;
					const { targetIds } = action;
					const pausableVideoIds = getPausableVideoIdsFromIdArray(sceneVideoIds, componentsById);

					for (let i = 0; i < targetIds.length; i++) {
						const targetId = targetIds[i];
						const targetVideo = componentsById[targetId] as IVideo;
						Jute.logEventToAnalytics({
							category: 'Video',
							name: `${triggerType} play video`,
							label: getAnalyticsLabelForAction(IActionCategory.playVideo, targetVideo.title),
							value: 1,
						});
						if (targetVideo.stopMedia) {
							entityActionDispatch?.({
								type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
								payload: { ids: pausableVideoIds },
							});
							entityActionDispatch?.({
								type: IEntityActionTypes.PLAY_VIDEOS_BY_ID,
								payload: { ids: [targetId], currentTime: 0 },
							});
							return;
						}
					}
					entityActionDispatch?.({
						type: IEntityActionTypes.PLAY_VIDEOS_BY_ID,
						payload: { ids: targetIds, currentTime: 0 },
					});
					break;
				}
				case IActionCategory.dialNumber: {
					if (!enterTransFinished) return;
					if (typeof action.tel === 'undefined') return;
					Jute.logEventToAnalytics({
						category: 'Dial number',
						name: `${triggerType} dial phone number`,
						label: getAnalyticsLabelForAction(IActionCategory.playVideo, entity.title),
						value: 1,
					});
					const { tel } = action;
					// If window couldn't be opened return value null

					if (Jute.isAppClip) {
						window.location.href = `tel:${tel}`;
					} else {
						const openVal = window.open(`tel:${tel}`, '_blank', 'noopener noreferrer');
						if (!openVal) document.location.href = `tel:${tel}`;
					}
					
					break;
				}
				case IActionCategory.linkPage: {
					if (!enterTransFinished) return;
					Jute.logEventToAnalytics({
						category: 'Link',
						name: `${triggerType} external link`,
						label: getAnalyticsLabelForAction(IActionCategory.linkPage, entity.title),
						value: 1,
					});
					let pageUrl = action.pageUrl;
					if (!pageUrl.toString().toLowerCase().startsWith('http')) {
						pageUrl = 'https://' + pageUrl;
					}
					if (pageUrl) {
						if (Jute.isAppClip) {
							window.location.href = pageUrl;
							return;
						}
						const a = document.createElement('a');
						a.href = pageUrl;
						if (Jute.isAppClip) {
							a.target = '_blank';
							a.rel = 'noopener noreferrer';
						}
						document.body.appendChild(a);
						a.click();
						document.body.removeChild(a);
					}
					break;
				}
				case IActionCategory.linkSocial: {
					Jute.logEventToAnalytics({
						category: 'Link',
						name: `${triggerType} external link`,
						label: getAnalyticsLabelForAction(IActionCategory.linkSocial, entity.title),
						value: 1,
					});
					if (!enterTransFinished) return;
					const { type, ...socialData } = action; // eslint-disable-line @typescript-eslint/no-unused-vars
					const [provider] = Object.keys(socialData || {});
					if (provider) {
						const socialUrl = socialData[provider as keyof typeof socialData];
						if (!socialUrl) return;
						const a = document.createElement('a');
						a.href = (socialData as any)[provider];
						if (Jute.isAppClip) a.target = '_blank';
						a.click();
					}
					break;
				}
				case IActionCategory.enlarge: {
					if (!enterTransFinished) return;
					Jute.logEventToAnalytics({
						category: 'Image',
						name: `${triggerType} enlarge image`,
						label: getAnalyticsLabelForAction(IActionCategory.enlarge, entity.title),
						value: 1,
					});
					userDispatch({
						type: IUserActions.SET_ENLARGED_IMG_ID,
						payload: action.entityId || ((entity as ISpatialComponentUnion).id ?? sceneId),
					});
					break;
				}
				case IActionCategory.saveContact: {
					const { type, ...contact } = action; // eslint-disable-line @typescript-eslint/no-unused-vars
					if (!contact) return;
					Jute.logEventToAnalytics({
						category: 'Save contact',
						name: `${triggerType} save contact`,
						label: getAnalyticsLabelForAction(IActionCategory.saveContact, entity.title),
						value: 1,
					});
					const vcardString = createVCardString(contact);
					const data = `data:text/vcard,${encodeURIComponent(vcardString)}`;

					let a = document.createElement('a');
					a.setAttribute('href', data);
					a.setAttribute('download', `${contact.name}.vcf`);
					a.click();
					break;
				}
				case IActionCategory.takePhoto: {
					// console.log('take photo');
					userDispatch({
						type: IUserActions.SET_SNAPSHOT_UI_VISIBLE,
						payload: true,
					});
					break;
				}
				case IActionCategory.animatePreset: {
					presetAnimationController
						.resetAllAnimationsByentityIdAndTrigger((entity as ISpatialComponentUnion).id ?? sceneId, triggerType, sceneId)
						.startAllAnimationsByEntityIdAndTrigger((entity as ISpatialComponentUnion).id ?? sceneId, triggerType, sceneId);
				}
				default:
					break;
			}
		}
	}, [componentsById, sceneId, isGrabAndGoEnabled, trackingImageDict]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const useGetEntityIsHidden = (id: string): boolean => {
	const hasAction = useDataStore((state) => {
		if (!state.componentsById) return false;
		const component = state.componentsById[id] as IButton;
		return !!component.actions;
	});
	const isSnapshotUiVisible = useUserStore((state) => state.isSnapshotUiVisible);
	return isSnapshotUiVisible && hasAction;
};

export const useResetEntityTransformsOnPlacement = (id: string) => {
	const { scene } = useThree();
	const placementMode = useSceneStore(({ placementMode }) => placementMode);

	useEffect(() => {
		if (!placementMode) return;
		resetEntityTransforms(id, scene)
	}, [placementMode])
}

export const useSceneHasRecordingButton = (sceneId: string) => {
	const componentsById = useDataStore(state => state.componentsById);
	if (!componentsById) return null;
	const scene = componentsById[sceneId] as ISceneComp;

	let sceneHasRecordingButton = false;

	for (const sceneComponentId of scene.children) {
		const sceneComponent = componentsById[sceneComponentId] as IComponentUnion;
		if (sceneComponent.type === IComponentType.ScreenContent) {
			for (const anchorId of sceneComponent.children) {
				const anchorGroup = componentsById[anchorId] as IScreenAnchorGroup;
				for (const srComponentId of anchorGroup.children) {
					const srComponent = componentsById[srComponentId] as ISpatialComponentUnion;
					if (srComponent.type === IComponentType.Button && srComponent.subCategory === IButtonSubCategory.recording) {
						sceneHasRecordingButton = true;
					}
				}
			}
		}
	}

	return sceneHasRecordingButton
}